/* global setTimeout */
import React from 'react'
import PropTypes from 'prop-types'
import debounce from 'lodash.debounce'
import { firstBy } from 'thenby'
import { TaxReturnTile } from '../../components/tax-return-tile'
import { dataSources } from '../../utils/data-sources'
import { PortalContainer } from '../../components/portal-container'
import { inject, observer } from 'mobx-react'
import { SessionStore } from '../../stores/session'
import Http from '../../services/http'
import { publish } from '../../utils/pubsub'
import { handleErrorsAPI } from '../../components/handle-errors'
import { getFraudHeaders } from '../../utils/get-fraud-headers'
import { PlaceholderCard } from '../../components/placeholder'

let myVar

@inject('sessionStore')
@observer
class TaxReturns extends React.Component {
  constructor (props) {
    super(props)
    this.http = Http.getInstance(this.props.sessionStore)
    this.dataSources = dataSources()
    this.onSearchChange = this.onSearchChange.bind(this)
    this.onSearchDebounced = debounce(() => this.search(), 250)
    this.searchInputRef = React.createRef()
    this.deleteReturnDialogRef = React.createRef()
    this.sortSelectRef = React.createRef()
    this.deleteButtonClick = this.deleteButtonClick.bind(this)
    this.deleteTaxReturn = this.deleteTaxReturn.bind(this)
    this.state = {
      ready: false,
      viewData: null,
      filteredData: null,
      query: null,
      inProgress: [],
      showConfirm: false,
      messageReturn: `By accepting this message you are confirming that you wish to completely delete all information that is currently entered/attached to this return.
                      \nDo you still want to delete this record?`,
      deleteReturnId: null
    }
  }

  loadData () {
    return this.http.getData(this.dataSources.taxReturns)
  }

  search () {
    const filter = this.state.query
    const matchesFilter = new RegExp(filter, 'i')
    const tempData = [...this.state.viewData]
    const filteredData = tempData.filter(item => !filter || matchesFilter.test(item.registrationNumber))
    this.setState({
      filteredData
    })
  }

  checkUpdatedStatus (item) {
    getFraudHeaders(this.props.sessionStore)
      .then((fraudHeaders) => {
        return this.http.getData(`${this.dataSources.taxReturnSubmit}/${item.id}`, {
          'headers': fraudHeaders
        })
      })
      .then((response) => {
        if (response.data.status === 'Submitted' || response.data.status === 'Submission failed') {
          const updatedData = {
            registrationNumber: item.TaxRegistration.registrationNumber,
            finishDate: item.periodEnd,
            startDate: item.periodStart,
            status: response.data.status,
            id: item.id
          }
          const newData = this.state.viewData.filter(ele => ele.id !== item.id)
          newData.push(updatedData)
          let inProgress = this.state.inProgress
          inProgress = inProgress.filter(i => i.id !== item.id)
          this.setState({
            viewData: newData,
            inProgress
          }, () => {
            if (this.state.inProgress.length === 0) {
              clearInterval(myVar)
            }
          })
        }
      })
  }

  componentDidMount () {
    this.loadData()
      .then((response) => {
        if (response) {
          const viewData = response.data.map(item => {
            if (item.status === 'Submission in progress') {
              const inProgress = this.state.inProgress
              inProgress.push(item)
              this.setState({
                inProgress
              })
              this.autoUpdate()
            }
            if (item.TaxRegistration) {
              return {
                registrationNumber: item.TaxRegistration.registrationNumber,
                registeredName: item.TaxRegistration.registeredName,
                finishDate: item.periodEnd,
                startDate: item.periodStart,
                status: item.status,
                id: item.id,
                taxRegistrationId: item.TaxRegistration.id,
                reconciliationId: (item.Reconciliation) ? item.Reconciliation.id : null
              }
            } else {
              return {
                registrationNumber: 'N/A',
                finishDate: item.periodEnd,
                startDate: item.periodStart,
                status: item.status,
                id: item.id
              }
            }
          })
          viewData.sort(
            firstBy('finishDate')
              .thenBy('registrationNumber')
          )
          viewData.sort((a, b) => new Date(b.startDate) - new Date(a.startDate))
          this.setState({
            viewData,
            ready: true
          }, () => {
            this.setup()
          })
        }
      })
      .catch(() => {
        this.setState({
          ready: true,
          viewData: []
        })
      })
  }

  setup = () => {
    this.searchInputRef.current.addEventListener('input', (e) => this.onSearchChange(e))
    this.deleteReturnDialogRef.current.addEventListener('submit', () => this.deleteTaxReturn())
    this.sortSelectRef.current.addEventListener('change', (e) => this.handleSortingOfReturns(e))
  }

  handleSortingOfReturns = (e) => {
    const viewData = [...this.state.viewData]
    if (e.target.value === 'old') {
      viewData.sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
    } else {
      viewData.sort((a, b) => new Date(b.startDate) - new Date(a.startDate))
    }
    this.setState({ viewData })
  }

  onSearchChange (e) {
    this.setState({
      query: e.target.value
    })
    this.onSearchDebounced()
  }

  autoUpdate () {
    myVar = setInterval(() => {
      if (this.state.inProgress.length > 0) {
        this.state.inProgress.map(item => {
          this.checkUpdatedStatus(item)
        })
      }
    }, 15000)
  }

  getData () {
    return this.state.filteredData ? this.state.filteredData : this.state.viewData
  }

  deleteButtonClick (id) {
    const taxReturn = this.state.viewData.find(item => item.id === id)
    if (!['Submitted', 'Submission in progress'].includes(taxReturn.status)) {
      this.setState({
        deleteReturnId: id
      }, () => {
        this.deleteReturnDialogRef.current.show()
      })
    } else {
      publish('toastr.danger', 'Cannot delete a tax return which is already submitted.')
    }
  }

  deleteTaxReturn () {
    this.deleteReturnDialogRef.current.close()
    this.http.deleteData(`${this.dataSources.taxReturnSummary}${this.state.deleteReturnId}`).then((response) => {
      if (response.status === 'success') {
        publish('toastr.success', 'Tax return successfully deleted')
        const renderReturn = this.state.viewData.filter(item => {
          return item.id !== this.state.deleteReturnId
        })
        this.setState({
          viewData: renderReturn,
          query: null,
          filteredData: null
        })
      } else {
        handleErrorsAPI(response)
      }
    })
  }

  getSearchComponent () {
    return (
      <PortalContainer parentContainer='btn-container'>
        <mosaic-form prevent-default>
          <mosaic-search-input
            placeholder='Search'
            ref={this.searchInputRef}
          >
            <mosaic-button
              class='mt-0 ml-0'
              icon='search'
              color='secondary'
            >
            </mosaic-button>

          </mosaic-search-input>

        </mosaic-form>
      </PortalContainer>
    )
  }

  deleteTaxReturnDialog = () => {
    return (
      <mosaic-form-dialog
        type='warning'
        header-title='Delete tax return'
        confirm-text = 'Yes'
        dismiss-text = 'No'
        ref={this.deleteReturnDialogRef}
      >
        <p>{this.state.messageReturn}</p>
      </mosaic-form-dialog>
    )
  }

  render () {
    if (this.state.ready) {
      const viewData = this.getData()
      if (viewData.length === 0) {
        return (
          <div>
            {this.getSearchComponent()}
            <p className='mb-0 p-3 p-lg-4'>There are no results related to your search.</p>
          </div>
        )
      } else {
        return (
          <div>
            {this.getSearchComponent()}
            <mosaic-row class='mt-2'>
              <mosaic-col>
                <mosaic-flex class='p-3'>
                  <div>
                    <mosaic-select-input
                      no-deselect
                      label='Sort by'
                      ref={this.sortSelectRef}
                    >

                      <option value = ''>Newest to oldest</option>
                      <option value = 'old'>Oldest to newest</option>
                    </mosaic-select-input>
                  </div>

                </mosaic-flex>

              </mosaic-col>
            </mosaic-row>

            <mosaic-row class='col-separator-1-3-4'>
              {viewData.map((r) => <TaxReturnTile key={r.id} {...r} reconciliationId={r.reconciliationId}
                registeredName={r.registeredName}
                registrationNumber={r.registrationNumber} taxRegistrationId={r.taxRegistrationId} deleteReturn={this.deleteButtonClick}
                isAdmin={this.props.sessionStore.isAdmin} isApprover={this.props.sessionStore.isApprover}/>)}
            </mosaic-row>
            {this.deleteTaxReturnDialog()}
          </div>
        )
      }
    } else {
      return <PlaceholderCard />
    }
  }
}

TaxReturns.propTypes = {
  sessionStore: PropTypes.instanceOf(SessionStore)
}

export { TaxReturns }
